/* eslint-disable no-nested-ternary */
import { Parser } from 'html-to-react'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { CheckBox } from '@/components/forms/CheckBox'
import { RichTextInput } from '@/components/forms/RichTextInput'
import { Select } from '@/components/forms/Select'
import { TrashIcon } from '@/components/icons'
import { Modal } from '@/components/ui/Modal'

/**
 *
 * RepairOrderActionItem
 *
 */
const RepairOrderActionItem = ({
  actionOptions,
  canEdit,
  canEditProblemWithoutTimestamps,
  isLineItemPending,
  isROServiceUser,
  item,
  loading,
  onDelete,
  onProblemChange,
  roIsOpen,
  saveActionNeeded,
  updateActionItemStatus,
}) => {
  // State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { control } = useForm({
    defaultValues: {
      problem: item.problem || '<p><br></p>',
    },
  })

  const matchingOption = actionOptions.find((option) => option.id === item.action?.id)

  // WARP-873: When the selected action's option is no longer active (e.g. its
  // ManufacturerAction was deactivated), it won't be present in `actionOptions`,
  // so `matchingOption` is undefined and the Select would render blank even
  // though the row still carries the expanded action. Fall back to the row's own
  // action so the real name shows instead of an empty dropdown.
  const selectedValue =
    matchingOption ?? (item.action?.id ? { ...item.action, label: item.action.name } : null)

  // A persisted row (real id, not a freshly-added `temp-*` placeholder) always
  // renders its problem, even if its action can't be resolved. Only brand-new
  // rows fall back to the "select an action first" prompt.
  const isNewItem = String(item.id).includes('temp')

  const isFieldDisabled = useMemo(() => {
    const isCompletedAndDifferentStatus =
      item.completedAt !== null &&
      ((roIsOpen && !matchingOption?.displayForOpenRo) ||
        (!roIsOpen && !matchingOption?.displayForClosedRo))
    return !canEdit || loading || !isLineItemPending || isCompletedAndDifferentStatus
  }, [item, canEdit, loading, isLineItemPending, matchingOption, roIsOpen])

  // WARP-840: Problem-only bypass for flagged WPC users. Lifts state, line-item
  // state, and completed-action gates from the Problem field — but NOT from the
  // action select, completed checkbox, or delete button (which keep using
  // isFieldDisabled). The `loading` guard is preserved so in-flight mutations
  // still gate the editor.
  const isProblemFieldDisabled = useMemo(
    () =>
      loading ||
      (!canEditProblemWithoutTimestamps && (isFieldDisabled || item.completedAt !== null)),
    [loading, canEditProblemWithoutTimestamps, isFieldDisabled, item.completedAt],
  )

  return (
    <div className="pb-2">
      <div className="grid grid-cols-1 items-start gap-2 md:grid-cols-2">
        {/* Action Needed (left column) */}
        <div className="relative flex-1 p-2 pl-0">
          <Select
            name={`action-needed-${item.id}`}
            id={`${item.id}`}
            value={selectedValue}
            placeholder="Select an option"
            options={actionOptions}
            className="bg-white-light rounded-md border-white"
            inputTextClassName="text-charcoal-900 text-md font-normal"
            onChange={(value) => saveActionNeeded(item.id, value.id, value.label)}
            disabled={isFieldDisabled || item.completedAt !== null}
          />
        </div>

        {/* Problem + Complete + Delete (right column) */}
        <div className="flex flex-row items-start gap-2">
          <div className="flex-1">
            {item.action?.id || !isNewItem ? (
              isROServiceUser ? (
                <div
                  data-testid={`actionProblemText-${item.id}`}
                  className="border-white-light text-charcoal-900 bg-white-light min-h-[80px] w-full cursor-not-allowed rounded-lg p-3"
                >
                  <p>{Parser().parse(item.problem || '<p><br></p>')}</p>
                </div>
              ) : (
                <Controller
                  name="problem"
                  control={control}
                  render={({ field }) => (
                    <RichTextInput
                      hideToolbar
                      id={`action-problem-${item.id}`}
                      disabled={isProblemFieldDisabled}
                      onChange={(change) => {
                        field.onChange(change)
                        onProblemChange(item.id, change)
                      }}
                      value={field.value}
                    />
                  )}
                />
              )
            ) : (
              <div className="text-charcoal-600 mt-2 flex min-h-[80px] items-start text-sm italic">
                Select an action to add a problem description
              </div>
            )}
          </div>
          <div className="flex w-20 shrink-0 items-center justify-center gap-2 pt-2">
            <CheckBox
              id={`action-completed-${item.id}`}
              name={`action-completed-${item.id}`}
              value={item.completedAt !== null}
              onChange={(event) => {
                updateActionItemStatus(item.id, event.target.checked)
              }}
              disabled={isFieldDisabled || item.id.includes('temp')}
            />
            {canEdit && (
              <button
                aria-label="Delete"
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                className="group inline-flex items-center justify-center rounded-lg bg-transparent text-sm font-medium text-gray-700 hover:bg-gray-100 focus:ring-4 focus:ring-gray-300 disabled:cursor-not-allowed"
                disabled={isFieldDisabled}
              >
                <TrashIcon className="text-charcoal-800 group-disabled:text-charcoal-600 h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
      <Modal
        open={isDeleteModalOpen}
        title="Are you sure?"
        setOpen={setIsDeleteModalOpen}
        content={
          <div className="my-8 flex h-full items-center justify-center">
            <p className="text-center">
              {`Please confirm that you'd like to delete this action item.`}
            </p>
          </div>
        }
        actions={[
          {
            type: 'submit',
            label: 'Delete',
            onClick: async () => {
              await onDelete(item.id)
              setIsDeleteModalOpen(false)
            },
          },
          { type: 'cancel', label: 'Cancel' },
        ]}
      />
    </div>
  )
}

export default RepairOrderActionItem

RepairOrderActionItem.propTypes = {
  actionOptions: PropTypes.array.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canEditProblemWithoutTimestamps: PropTypes.bool,
  isLineItemPending: PropTypes.bool.isRequired,
  isROServiceUser: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onProblemChange: PropTypes.func.isRequired,
  roIsOpen: PropTypes.bool.isRequired,
  saveActionNeeded: PropTypes.func.isRequired,
  updateActionItemStatus: PropTypes.func.isRequired,
}

RepairOrderActionItem.defaultProps = {
  canEditProblemWithoutTimestamps: false,
}
